import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import { TrendingDown, TrendingUp } from "@material-ui/icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import Tooltip from "@material-ui/core/Tooltip";
import { ThreeDots } from "react-loader-spinner";

import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import Pnl from "components/Pnl/Pnl";
import { PnlSharedProvider } from "hooks/usePnlShared";
import useThemeColors from "hooks/useThemeColors";

import alignmentSessionStyles from "./styles";
import IndicatorsColumn from "./IndicatorsColumn";
import KeyMessagesPanel from "./KeyMessagesPanel";
import FocusModeWrapper from "./FocusModeWrapper";
import { usePnlConfig } from "./hooks";
import { getMonthAbbr } from "./utils";

import {
  blendColors,
  whiteColor,
} from "assets/jss/material-dashboard-pro-react";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ChartDataLabels,
);

const useStyles = makeStyles(alignmentSessionStyles);

/**
 * HighlightsSection component
 * Displays the highlights/trends page with indicators, P&L comparison,
 * waterfall chart, and margin drivers.
 *
 * Data comes from the Data Orchestrator via the same endpoints used by
 * the Trends page (results_pl_explorer_read_pl + results_trends_drivers).
 */
function HighlightsSection({
  clientColor: propsClientColor,
  activeSession,
  pnlMetadata,
  dataOrchestrator,
  focusMode = false,
}) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { clientColor: themeClientColor } = useThemeColors();
  const clientColor = propsClientColor || themeClientColor;

  // ========================= Data from orchestrator =========================

  // Get highlights data for the currently selected period.
  // The orchestrator loads data automatically — no on-demand trigger needed.
  const highlightsData = dataOrchestrator?.getHighlightsData?.() || null;
  const plValues = highlightsData?.plValues || {};
  const driversData = highlightsData?.driversData || null;

  const isDataLoading = !highlightsData;

  // ========================= P&L config =========================

  const {
    plStructure,
    netRevenueAnchor,
    serveMarginAnchor,
    volumeUnit,
    volumeL12M,
    oom,
    staticPnlProps,
    drillDownsOpen,
    setDrillDownsOpen,
    mouseOverPLLine,
    mouseOverPLLineFade,
    timersAllowed,
    handleMouseOverPLLine,
    handleMouseLeavePLLine,
  } = usePnlConfig({
    translateStructure: true,
    pnlMetadata,
    plValues,
  });

  // ========================= P&L names =========================

  const plNames = useMemo(() => {
    const periods = dataOrchestrator?.getComparisonPeriods?.();
    if (!periods || !periods.previous?.length || !periods.current?.length)
      return { 1: "-", 2: "-" };

    const fmtRange = (arr) => {
      if (!arr || arr.length === 0) return "-";
      const sorted = [...arr].sort(
        (a, b) => a[0] * 100 + a[1] - (b[0] * 100 + b[1]),
      );
      if (sorted.length === 1) {
        const [y, m] = sorted[0];
        return `${getMonthAbbr(m - 1, t)}/${String(y).slice(-2)}`;
      }
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      return `${getMonthAbbr(first[1] - 1, t)}/${String(first[0]).slice(-2)} a ${getMonthAbbr(last[1] - 1, t)}/${String(last[0]).slice(-2)}`;
    };

    return { 1: fmtRange(periods.previous), 2: fmtRange(periods.current) };
  }, [dataOrchestrator, t]);

  // ========================= Indicators =========================

  const grossRevenueAnchor = useMemo(() => {
    const options = pnlMetadata?.pl?.revenue_reference_options || [];
    const firstOption = options.find((o) => o.value !== "combined");
    return firstOption?.value || "gross_revenue";
  }, [pnlMetadata]);

  const indicators = useMemo(() => {
    if (!plValues[1] || !plValues[2] || !Object.keys(plValues[1]).length) {
      return null;
    }

    const vol1 = plValues[1]?.volume;
    const vol2 = plValues[2]?.volume;
    const gr1 = plValues[1]?.[grossRevenueAnchor];
    const gr2 = plValues[2]?.[grossRevenueAnchor];
    const nr1 = plValues[1]?.[netRevenueAnchor.current];
    const nr2 = plValues[2]?.[netRevenueAnchor.current];
    const sm1 = plValues[1]?.[serveMarginAnchor.current];
    const sm2 = plValues[2]?.[serveMarginAnchor.current];

    if (!vol1 || !vol2 || !gr1 || !gr2 || !nr1 || !nr2) return null;

    const volDeltaPct = ((vol2 - vol1) / vol1) * 100;
    const avgPrice1 = gr1 / vol1;
    const avgPrice2 = gr2 / vol2;
    const priceDeltaPct = ((avgPrice2 - avgPrice1) / avgPrice1) * 100;
    const nrDeltaPct = ((nr2 - nr1) / nr1) * 100;
    const smDelta = (sm2 || 0) - (sm1 || 0);
    const smPct1 = nr1 ? (100 * (sm1 || 0)) / nr1 : 0;
    const smPct2 = nr2 ? (100 * (sm2 || 0)) / nr2 : 0;
    const smDeltaPp = smPct2 - smPct1;

    const volUnit = volumeUnit.current || "kg";
    const div = oom.div || 1e6;
    const suffix = oom.x || "";

    const fmt = (v, d = 1) => (v >= 0 ? "+" : "") + v.toFixed(d);

    return [
      {
        label: t("alignment_session.indicators.delta_volume"),
        value: fmt(volDeltaPct) + "%",
        subtext:
          fmt(vol2 - vol1 > 0 ? (vol2 - vol1) / div : (vol2 - vol1) / div) +
          suffix +
          " " +
          volUnit,
        positive: volDeltaPct >= 0,
      },
      {
        label: t("alignment_session.indicators.delta_gross_price"),
        value: fmt(priceDeltaPct) + "%",
        subtext: `${avgPrice1.toFixed(2)} → ${avgPrice2.toFixed(2)} /${volUnit}`,
        positive: priceDeltaPct >= 0,
      },
      {
        label: t("alignment_session.indicators.delta_net_revenue"),
        value: fmt(nrDeltaPct) + "%",
        subtext: fmt((nr2 - nr1) / div) + suffix,
        positive: nrDeltaPct >= 0,
      },
      {
        label: t("alignment_session.indicators.delta_serve_margin"),
        value: fmt(smDelta / div) + suffix,
        subtext: `${smPct1.toFixed(1)}% → ${smPct2.toFixed(1)}%`,
        positive: smDelta >= 0,
      },
      {
        label: t("alignment_session.indicators.delta_serve_margin_pct"),
        value: fmt(smDeltaPp, 1) + " pp",
        subtext:
          t("alignment_session.indicators.on_net_revenue") ||
          "s/ Receita Líquida",
        positive: smDeltaPp >= 0,
      },
    ];
  }, [
    plValues,
    grossRevenueAnchor,
    netRevenueAnchor,
    serveMarginAnchor,
    volumeUnit,
    oom,
    t,
  ]);

  // Placeholder indicators while loading
  const loadingIndicators = useMemo(
    () => [
      {
        label: t("alignment_session.indicators.delta_volume"),
        value: "-",
        subtext: "-",
        positive: true,
      },
      {
        label: t("alignment_session.indicators.delta_gross_price"),
        value: "-",
        subtext: "-",
        positive: true,
      },
      {
        label: t("alignment_session.indicators.delta_net_revenue"),
        value: "-",
        subtext: "-",
        positive: true,
      },
      {
        label: t("alignment_session.indicators.delta_serve_margin"),
        value: "-",
        subtext: "-",
        positive: true,
      },
      {
        label: t("alignment_session.indicators.delta_serve_margin_pct"),
        value: "-",
        subtext: "-",
        positive: true,
      },
    ],
    [t],
  );

  // ========================= Waterfall =========================

  const waterfallData = useMemo(() => {
    if (!plValues[1] || !plValues[2]) return null;

    const volume1 = plValues[1]?.volume;
    const volume2 = plValues[2]?.volume;
    const grossRevenue1 = plValues[1]?.[grossRevenueAnchor];
    const grossRevenue2 = plValues[2]?.[grossRevenueAnchor];
    const netRevenue1 = plValues[1]?.[netRevenueAnchor.current];
    const netRevenue2 = plValues[2]?.[netRevenueAnchor.current];
    const serveMargin1 = plValues[1]?.[serveMarginAnchor.current];
    const serveMargin2 = plValues[2]?.[serveMarginAnchor.current];

    if (
      !volume1 ||
      !volume2 ||
      !grossRevenue1 ||
      !grossRevenue2 ||
      !netRevenue1 ||
      !netRevenue2 ||
      !serveMargin1 ||
      !serveMargin2
    )
      return null;

    const marginPercent1 = (100 * serveMargin1) / netRevenue1;
    const marginPercent2 = (100 * serveMargin2) / netRevenue2;

    const deltaSM = serveMargin2 - serveMargin1;
    const deltaVol = volume2 - volume1;
    const marginUnit1 = serveMargin1 / volume1;
    const revenueUnit1 = netRevenue1 / volume1;
    const revenueUnit2 = netRevenue2 / volume2;

    const denominatorEffect =
      serveMargin1 * (1 / netRevenue2 - 1 / netRevenue1) * 100;
    const interactionEffect =
      deltaSM * (1 / netRevenue2 - 1 / netRevenue1) * 100;

    const deltaSM_mix = deltaVol * marginUnit1;
    const grossPriceUnit1 = grossRevenue1 / volume1;
    const grossPriceUnit2 = grossRevenue2 / volume2;
    const costUnit1 = (grossRevenue1 - serveMargin1) / volume1;
    const costUnit2 = (grossRevenue2 - serveMargin2) / volume2;
    const deltaSM_price = volume2 * (grossPriceUnit2 - grossPriceUnit1);
    const deltaSM_cost = volume2 * (costUnit1 - costUnit2);

    const numPrice = (deltaSM_price / netRevenue1) * 100;
    const numCost = (deltaSM_cost / netRevenue1) * 100;
    const numMix = (deltaSM_mix / netRevenue1) * 100;

    const deltaNR_volume = deltaVol * revenueUnit1;
    const deltaNR_price = volume2 * (revenueUnit2 - revenueUnit1);
    const totalDeltaNR_abs = Math.abs(deltaNR_volume) + Math.abs(deltaNR_price);

    let denomToPrice = 0;
    let denomToMix = 0;
    if (totalDeltaNR_abs > 0.001) {
      denomToPrice =
        denominatorEffect * (Math.abs(deltaNR_price) / totalDeltaNR_abs);
      denomToMix =
        denominatorEffect * (Math.abs(deltaNR_volume) / totalDeltaNR_abs);
    } else {
      denomToPrice = denominatorEffect / 2;
      denomToMix = denominatorEffect / 2;
    }

    const priceEffectRaw = numPrice + denomToPrice;
    const costEffectRaw = numCost;
    const mixEffectRaw = numMix + denomToMix;

    const totalAbs =
      Math.abs(priceEffectRaw) +
      Math.abs(costEffectRaw) +
      Math.abs(mixEffectRaw);

    let priceEffect, costEffect, mixEffect;
    if (totalAbs > 0) {
      priceEffect =
        priceEffectRaw +
        interactionEffect * (Math.abs(priceEffectRaw) / totalAbs);
      costEffect =
        costEffectRaw +
        interactionEffect * (Math.abs(costEffectRaw) / totalAbs);
      mixEffect =
        mixEffectRaw + interactionEffect * (Math.abs(mixEffectRaw) / totalAbs);
    } else {
      priceEffect = interactionEffect / 3;
      costEffect = interactionEffect / 3;
      mixEffect = interactionEffect / 3;
    }

    return {
      marginInitial: marginPercent1,
      marginFinal: marginPercent2,
      totalDelta: marginPercent2 - marginPercent1,
      priceEffect,
      costEffect,
      mixEffect,
    };
  }, [plValues, grossRevenueAnchor, netRevenueAnchor, serveMarginAnchor]);

  // ========================= Drivers =========================

  const losses = driversData?.main_losses || [];
  const gains = driversData?.main_gains || [];

  const maxDriverImpact = useMemo(() => {
    const allImpacts = [
      ...losses.map((d) => Math.abs(d.total_impact)),
      ...gains.map((d) => Math.abs(d.total_impact)),
    ];
    if (allImpacts.length === 0) return 1;
    return Math.max(...allImpacts);
  }, [losses, gains]);

  const getRelevanceArrow = useCallback(
    (totalImpact) => {
      const absImpact = Math.abs(totalImpact);
      const isNegative = totalImpact < 0;

      const highThreshold = maxDriverImpact * 0.66;
      const mediumThreshold = maxDriverImpact * 0.33;

      let angle, color, relevanceLevel;

      if (absImpact >= highThreshold) {
        angle = isNegative ? 90 : -90;
        color = isNegative ? "#c62828" : "#2e7d32";
        relevanceLevel = "high";
      } else if (absImpact >= mediumThreshold) {
        angle = isNegative ? 60 : -60;
        color = isNegative ? "#f57c00" : "#7cb342";
        relevanceLevel = "medium";
      } else {
        angle = isNegative ? 30 : -30;
        color = isNegative ? "#757575" : "#9e9e9e";
        relevanceLevel = "low";
      }

      return { angle, color, relevanceLevel };
    },
    [maxDriverImpact],
  );

  // Build narrative for a driver (simplified version from Trends.js)
  const buildDriverNarrative = useCallback(
    (driver) => {
      const {
        element,
        dimension_label,
        dimension,
        effect_type,
        avg_price_p1,
        avg_price_p2,
        margin_pct_p1,
        margin_pct_p2,
        volume_share_p1,
        volume_share_p2,
        cost_line_pct_p1,
        cost_line_pct_p2,
        dominant_cost_line_name,
        cost_pct_p1,
        cost_pct_p2,
      } = driver;

      // Dimension name
      let dimName = "";
      if (dimension_label) {
        dimName =
          typeof dimension_label === "string"
            ? dimension_label
            : dimension_label[i18n.language] || dimension_label.en || "";
      }
      if (!dimName && dimension) {
        dimName =
          typeof dimension === "string" ? dimension : dimension.en || "";
      }

      const elementDisplay =
        element === "" || element == null ? t("results.blank") : element;

      const fmtPct = (v) =>
        v === null || v === undefined ? "—" : `${v.toFixed(1)}%`;

      let afterElement = "";

      if (effect_type === "price") {
        const change =
          avg_price_p1 && avg_price_p1 !== 0
            ? ((avg_price_p2 - avg_price_p1) / avg_price_p1) * 100
            : 0;
        const causeText =
          change > 0
            ? t("results.trends.price_increase")
            : t("results.trends.price_decrease");
        afterElement = ` → <strong>${causeText}</strong> ${t("results.trends.of")} <strong>${fmtPct(Math.abs(change))}</strong>`;
      } else if (effect_type === "cost") {
        let costIncreased;
        let costLineDetail = "";
        if (
          dominant_cost_line_name &&
          cost_line_pct_p1 !== null &&
          cost_line_pct_p2 !== null
        ) {
          costIncreased = cost_line_pct_p2 > cost_line_pct_p1;
          costLineDetail = ` ${t("results.trends.in")} '${dominant_cost_line_name}' (${fmtPct(cost_line_pct_p1)} › ${fmtPct(cost_line_pct_p2)})`;
        } else {
          costIncreased = (cost_pct_p2 || 0) > (cost_pct_p1 || 0);
        }
        const causeText = costIncreased
          ? t("results.trends.cost_increase")
          : t("results.trends.cost_decrease");
        afterElement = ` → <strong>${causeText}</strong>${costLineDetail}`;
      } else if (effect_type === "mix") {
        const s1 = (volume_share_p1 || 0) * 100;
        const s2 = (volume_share_p2 || 0) * 100;
        const causeText =
          s2 > s1
            ? t("results.trends.participation_increase")
            : t("results.trends.participation_decrease");
        const globalMarginP2 = driversData?.global_margin_pct_p2 || 0;
        const marginContext =
          margin_pct_p2 > globalMarginP2
            ? t("results.trends.margin_above_average")
            : t("results.trends.margin_below_average");
        afterElement = ` → <strong>${causeText}</strong> (${fmtPct(s1)} › ${fmtPct(s2)}) ${marginContext}`;
      } else {
        afterElement = ` → <strong>Δ</strong> (${fmtPct(margin_pct_p1)} › ${fmtPct(margin_pct_p2)})`;
      }

      return { dimName, elementDisplay, afterElement };
    },
    [i18n.language, t, driversData],
  );

  // State for key messages
  const [keyMessages, setKeyMessages] = useState([]);

  // ========================= Spinners =========================

  const isPlRefreshing = isDataLoading && !!activeSession;

  const driverSpinner = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <ThreeDots width="40px" color={clientColor} />
    </div>
  );

  // ========================= Render driver row =========================

  const renderDriver = useCallback(
    (driver, idx) => {
      const isNegative = driver.total_impact < 0;
      const arrow = getRelevanceArrow(driver.total_impact);
      const narrative = buildDriverNarrative(driver);

      return (
        <div
          key={idx}
          className={classes.driverRow}
          style={{ borderLeftColor: isNegative ? "#d32f2f" : "#388e3c" }}
        >
          <Tooltip
            title={
              arrow.relevanceLevel === "high"
                ? t("results.trends.relevance.high_tooltip", {
                    defaultValue: "Alto impacto",
                  })
                : arrow.relevanceLevel === "medium"
                  ? t("results.trends.relevance.medium_tooltip", {
                      defaultValue: "Médio impacto",
                    })
                  : t("results.trends.relevance.low_tooltip", {
                      defaultValue: "Baixo impacto",
                    })
            }
            placement="top"
          >
            <div className={classes.driverArrow}>
              <FontAwesomeIcon
                icon={faArrowRight}
                style={{
                  transform: `rotate(${arrow.angle}deg)`,
                  color: arrow.color,
                  fontSize: "14px",
                  transition: "transform 0.3s",
                }}
              />
            </div>
          </Tooltip>
          <div className={classes.driverNarrative}>
            {narrative.dimName && (
              <span style={{ color: "#777" }}>{narrative.dimName} </span>
            )}
            <span className={classes.driverElement}>
              {narrative.elementDisplay}
            </span>
            <span
              dangerouslySetInnerHTML={{ __html: narrative.afterElement }}
            />
          </div>
        </div>
      );
    },
    [getRelevanceArrow, buildDriverNarrative, classes, t],
  );

  // ========================= Render =========================

  return (
    <FocusModeWrapper focusMode={focusMode}>
      <div
        className={
          focusMode
            ? classes.highlightsContainerFocus
            : classes.highlightsContainer
        }
        style={focusMode ? { height: "100%", marginTop: 0 } : {}}
      >
        <div
          className={
            focusMode
              ? classes.highlightsMainLayoutFocus
              : classes.highlightsMainLayout
          }
        >
          {/* Indicators Column */}
          <IndicatorsColumn indicators={indicators || loadingIndicators} />

          {/* Main Content - 2 columns (50/50) */}
          <div className={classes.highlightsContent}>
            {/* Left Column - Waterfall and Drivers */}
            <div className={classes.highlightsLeftColumn}>
              {/* Building Blocks Waterfall Chart */}
              <Card className={classes.waterfallCard}>
                <div
                  className={classes.sectionTitleCardHeader}
                  style={{
                    borderBottom:
                      "1px solid " + blendColors(clientColor, whiteColor, 0.25),
                  }}
                >
                  {t("alignment_session.highlights.waterfall_title")}
                </div>
                <CardBody className={classes.waterfallCardBody}>
                  <div className={classes.waterfallChartWrapper}>
                    {isDataLoading && driverSpinner}
                    {!isDataLoading && waterfallData && (
                      <Bar
                        data={{
                          labels: [
                            t("results.trends.initial_margin"),
                            t("results.trends.price_delta"),
                            t("results.trends.cost_delta"),
                            t("results.trends.mix_delta"),
                            t("results.trends.final_margin"),
                          ],
                          datasets: [
                            {
                              label: "Values",
                              data: [
                                [0, waterfallData.marginInitial],
                                [
                                  waterfallData.marginInitial,
                                  waterfallData.marginInitial +
                                    waterfallData.priceEffect,
                                ],
                                [
                                  waterfallData.marginInitial +
                                    waterfallData.priceEffect,
                                  waterfallData.marginInitial +
                                    waterfallData.priceEffect +
                                    waterfallData.costEffect,
                                ],
                                [
                                  waterfallData.marginInitial +
                                    waterfallData.priceEffect +
                                    waterfallData.costEffect,
                                  waterfallData.marginInitial +
                                    waterfallData.priceEffect +
                                    waterfallData.costEffect +
                                    waterfallData.mixEffect,
                                ],
                                [0, waterfallData.marginFinal],
                              ],
                              backgroundColor: function (context) {
                                const index = context.dataIndex;
                                if (index === 0 || index === 4)
                                  return clientColor;
                                const value = context.raw;
                                const delta = value[1] - value[0];
                                return delta >= 0 ? "#66BB6A" : "#EF5350";
                              },
                              borderWidth: 0,
                              borderRadius: 6,
                              borderSkipped: function (context) {
                                const value = context.raw;
                                const delta = value[1] - value[0];
                                return delta >= 0 ? "bottom" : "top";
                              },
                              datalabels: {
                                display: true,
                                color: function (context) {
                                  return context.dataIndex === 0 ||
                                    context.dataIndex === 4
                                    ? "#ffffff"
                                    : "#333";
                                },
                                anchor: "center",
                                align: "center",
                                font: {
                                  family:
                                    '"Roboto", "Helvetica", "Arial", sans-serif',
                                  weight: "300",
                                  size: 14,
                                },
                                formatter: function (value, context) {
                                  const delta = value[1] - value[0];
                                  if (
                                    context.dataIndex === 0 ||
                                    context.dataIndex === 4
                                  ) {
                                    return value[1].toFixed(1) + " p.p.";
                                  }
                                  return (
                                    (delta >= 0 ? "+" : "") +
                                    delta.toFixed(1) +
                                    " p.p."
                                  );
                                },
                              },
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { display: false },
                            tooltip: { enabled: false },
                          },
                          scales: {
                            x: {
                              border: { display: false },
                              grid: { display: false },
                              ticks: {
                                font: {
                                  family:
                                    '"Roboto", "Helvetica", "Arial", sans-serif',
                                  weight: "300",
                                  size: 11,
                                },
                              },
                            },
                            y: {
                              beginAtZero: true,
                              border: { display: false },
                              ticks: { display: false },
                              grid: {
                                display: true,
                                drawBorder: false,
                                drawTicks: false,
                                color: function (context) {
                                  return context.tick.value === 0
                                    ? "#999"
                                    : "transparent";
                                },
                                lineWidth: 1,
                              },
                            },
                          },
                        }}
                      />
                    )}
                  </div>
                </CardBody>
              </Card>

              {/* Losses Card */}
              <Card className={classes.driversCard}>
                <div
                  className={classes.sectionTitleCardHeaderWithIcon}
                  style={{ color: "#d32f2f" }}
                >
                  <TrendingDown style={{ color: "#d32f2f" }} />
                  {t("alignment_session.highlights.drivers_losses")}
                </div>
                <CardBody style={{ padding: 0 }}>
                  <div className={classes.driversSection}>
                    {isDataLoading && driverSpinner}
                    {!isDataLoading &&
                      losses.map((driver, idx) => renderDriver(driver, idx))}
                    {!isDataLoading && losses.length === 0 && (
                      <div
                        style={{
                          padding: "16px",
                          textAlign: "center",
                          color: "#999",
                        }}
                      >
                        {t("results.no_data_available")}
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>

              {/* Gains Card */}
              <Card className={classes.driversCard}>
                <div
                  className={classes.sectionTitleCardHeaderWithIcon}
                  style={{ color: "#388e3c" }}
                >
                  <TrendingUp style={{ color: "#388e3c" }} />
                  {t("alignment_session.highlights.drivers_gains")}
                </div>
                <CardBody style={{ padding: 0 }}>
                  <div className={classes.driversSection}>
                    {isDataLoading && driverSpinner}
                    {!isDataLoading &&
                      gains.map((driver, idx) => renderDriver(driver, idx))}
                    {!isDataLoading && gains.length === 0 && (
                      <div
                        style={{
                          padding: "16px",
                          textAlign: "center",
                          color: "#999",
                        }}
                      >
                        {t("results.no_data_available")}
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* Right Column - P&Ls */}
            <div className={classes.highlightsRightColumn}>
              <PnlSharedProvider
                plValues={plValues}
                plStructure={plStructure}
                benchmark={null}
                netRevenueAnchor={netRevenueAnchor}
                serveMarginAnchor={serveMarginAnchor}
                oom={oom}
                t={t}
              >
                <div className={classes.plCardsRow}>
                  {/* P&L Names */}
                  <Card className={classes.plCardHighlights}>
                    <CardBody className={classes.plCardBody}>
                      <Pnl
                        plStructure={plStructure}
                        netRevenueAnchor={netRevenueAnchor}
                        serveMarginAnchor={serveMarginAnchor}
                        numPL={1}
                        numPLs={2}
                        plName={"-"}
                        handleMouseLeavePLLine={handleMouseLeavePLLine}
                        handleMouseOverPLLine={handleMouseOverPLLine}
                        mouseOverPLLine={mouseOverPLLine}
                        mouseOverPLLineFade={mouseOverPLLineFade}
                        timersAllowed={timersAllowed.current}
                        drillDownsOpen={drillDownsOpen}
                        setDrillDownsOpen={setDrillDownsOpen}
                        plValues={plValues}
                        volumeUnit={volumeUnit}
                        volumeL12M={volumeL12M}
                        oom={oom}
                        showColumns={["name"]}
                        fixedHighlightLine={serveMarginAnchor.current}
                        {...staticPnlProps}
                        isRefreshing={isPlRefreshing}
                      />
                    </CardBody>
                  </Card>

                  {/* P&L Previous YTD */}
                  <Card className={classes.plCardHighlights}>
                    <CardBody className={classes.plCardBody}>
                      <Pnl
                        plStructure={plStructure}
                        netRevenueAnchor={netRevenueAnchor}
                        serveMarginAnchor={serveMarginAnchor}
                        numPL={1}
                        numPLs={2}
                        plName={plNames[1]}
                        handleMouseLeavePLLine={handleMouseLeavePLLine}
                        handleMouseOverPLLine={handleMouseOverPLLine}
                        mouseOverPLLine={mouseOverPLLine}
                        mouseOverPLLineFade={mouseOverPLLineFade}
                        timersAllowed={timersAllowed.current}
                        drillDownsOpen={drillDownsOpen}
                        setDrillDownsOpen={setDrillDownsOpen}
                        plValues={plValues}
                        volumeUnit={volumeUnit}
                        volumeL12M={volumeL12M}
                        oom={oom}
                        showColumns={["val", "percentage"]}
                        fixedHighlightLine={serveMarginAnchor.current}
                        {...staticPnlProps}
                        isRefreshing={isPlRefreshing}
                        plsToRefresh={isPlRefreshing ? [1] : []}
                      />
                    </CardBody>
                  </Card>

                  {/* P&L Current YTD */}
                  <Card className={classes.plCardHighlights}>
                    <CardBody className={classes.plCardBody}>
                      <Pnl
                        plStructure={plStructure}
                        netRevenueAnchor={netRevenueAnchor}
                        serveMarginAnchor={serveMarginAnchor}
                        numPL={2}
                        numPLs={2}
                        plName={plNames[2]}
                        handleMouseLeavePLLine={handleMouseLeavePLLine}
                        handleMouseOverPLLine={handleMouseOverPLLine}
                        mouseOverPLLine={mouseOverPLLine}
                        mouseOverPLLineFade={mouseOverPLLineFade}
                        timersAllowed={timersAllowed.current}
                        drillDownsOpen={drillDownsOpen}
                        setDrillDownsOpen={setDrillDownsOpen}
                        plValues={plValues}
                        volumeUnit={volumeUnit}
                        volumeL12M={volumeL12M}
                        oom={oom}
                        showColumns={["val", "percentage"]}
                        fixedHighlightLine={serveMarginAnchor.current}
                        {...staticPnlProps}
                        isRefreshing={isPlRefreshing}
                        plsToRefresh={isPlRefreshing ? [2] : []}
                      />
                    </CardBody>
                  </Card>

                  {/* P&L Delta */}
                  <Card className={classes.plCardHighlights}>
                    <CardBody className={classes.plCardBody}>
                      <Pnl
                        plStructure={plStructure}
                        netRevenueAnchor={netRevenueAnchor}
                        serveMarginAnchor={serveMarginAnchor}
                        numPL={2}
                        numPLs={2}
                        plName={"Δ"}
                        handleMouseLeavePLLine={handleMouseLeavePLLine}
                        handleMouseOverPLLine={handleMouseOverPLLine}
                        mouseOverPLLine={mouseOverPLLine}
                        mouseOverPLLineFade={mouseOverPLLineFade}
                        timersAllowed={timersAllowed.current}
                        drillDownsOpen={drillDownsOpen}
                        setDrillDownsOpen={setDrillDownsOpen}
                        plValues={plValues}
                        volumeUnit={volumeUnit}
                        volumeL12M={volumeL12M}
                        oom={oom}
                        showColumns={["delta"]}
                        showDelta={true}
                        deltaBaseNumPL={1}
                        fixedHighlightLine={serveMarginAnchor.current}
                        {...staticPnlProps}
                        isRefreshing={isPlRefreshing}
                        plsToRefresh={isPlRefreshing ? [2] : []}
                      />
                    </CardBody>
                  </Card>
                </div>
              </PnlSharedProvider>
            </div>
          </div>
        </div>

        {/* Key Messages Panel */}
        <KeyMessagesPanel
          messages={keyMessages}
          onMessagesChange={setKeyMessages}
        />
      </div>
    </FocusModeWrapper>
  );
}

HighlightsSection.propTypes = {
  clientColor: PropTypes.string,
  activeSession: PropTypes.object,
  pnlMetadata: PropTypes.object,
  dataOrchestrator: PropTypes.object,
  focusMode: PropTypes.bool,
};

HighlightsSection.defaultProps = {
  clientColor: null,
  activeSession: null,
  pnlMetadata: null,
  dataOrchestrator: null,
  focusMode: false,
};

export default HighlightsSection;
